import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { useIsMobile } from "../../hooks/useIsMobile";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "pwa_install_dismissed_at";
const DISMISS_DAYS = 14;

const readDismissed = () => {
  try {
    const stored = Number(localStorage.getItem(DISMISSED_KEY));
    return stored > 0 && Date.now() - stored < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    return;
  }
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIosSafari = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios|edgios/i.test(navigator.userAgent);

export function InstallPrompt() {
  const { t } = useTranslation("common");
  const isMobile = useIsMobile();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [standalone] = useState(isStandalone);
  const [iconUrl] = useState(
    () => document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')?.href,
  );

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const showIos = isIosSafari();
  if (!isMobile || standalone || dismissed || (!deferred && !showIos)) return null;

  const dismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    outcome === "dismissed" && dismiss();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-safe-bottom">
      <div className="mb-3 flex items-start gap-3 rounded-2xl border border-line bg-card p-3 shadow-dropdown">
        {iconUrl && <img src={iconUrl} alt="" className="h-10 w-10 shrink-0 rounded-xl" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-content">
            {deferred ? t("pwaInstall.title") : t("pwaInstall.iosTitle")}
          </p>
          <p className="mt-0.5 text-xs text-content-secondary">
            {deferred ? t("pwaInstall.description") : t("pwaInstall.iosStep")}
          </p>
          <div className="mt-2 flex items-center gap-2">
            {deferred && (
              <Button size="sm" onClick={install}>
                {t("pwaInstall.install")}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss}>
              {t("pwaInstall.dismiss")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
